import React from 'react';

import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { useMediaList } from '@lumapps/medias/hooks/useMediaList';
import { useSelector } from '@lumapps/redux/react';

import { type ListDocumentsParams } from '../api';
import type { DocumentFiltersType } from '../types';

export interface UseFolderContentsParams
    extends Partial<Pick<ListDocumentsParams, 'searchTags' | 'searchTypes' | 'sortOrder'>> {
    /** The ID of the folder whose contents are to be fetched. If null, fetches root content. */
    folderId: string | null;
    /** If `false`, the hook will not fetch any data. */
    isEnabled: boolean;
}

/**
 * A low-level hook responsible for fetching the contents of a specific folder.
 * This hook encapsulates the data-fetching and API logic for folder contents.
 *
 * @param {UseFolderContentsParams} params
 * @returns {object} An object containing the list of items, loading/error states, and pagination functions.
 */
export const useFolderContents = ({
    folderId,
    isEnabled,
    searchTags,
    searchTypes,
    sortOrder,
}: UseFolderContentsParams) => {
    const currentSiteId = useSelector(instanceIdSelector);

    const [sorts, setSorts] = React.useState<ListDocumentsParams['sortOrder'] | undefined>(sortOrder);
    const [searchText, setSearchText] = React.useState<string>('');
    const [tagFilters, setTagFilters] = React.useState<ListDocumentsParams['searchTags']>(searchTags || []);

    const currentPath = React.useMemo(() => {
        const baseDocPath = `provider=local/site=${currentSiteId}`;
        return folderId ? `${baseDocPath}/resource=${folderId}` : baseDocPath;
    }, [currentSiteId, folderId]);

    const queryParams = React.useMemo(
        (): ListDocumentsParams => ({
            docPath: currentPath,
            searchInFolder: false,
            searchParameters: { shared: true, trashed: false, recursive: null },
            searchTags: tagFilters,
            searchText,
            searchTypes,
            sortOrder: sorts,
        }),
        [currentPath, searchText, searchTypes, sorts, tagFilters],
    );

    const { fetchNextPage, ...mediaListRest } = useMediaList(queryParams, { enabled: isEnabled });

    /**
     * Fetches the next page of media if available.
     */
    const handleFetchNextPage = () => {
        if (mediaListRest.hasNextPage) {
            fetchNextPage();
        }
    };

    /**
     * Callback to be executed when the search query has been changed.
     *
     * @param {string} query
     */
    const onSearch = (query: string) => {
        if (query !== searchText) {
            setSearchText(query);
        }
    };

    /**
     * Callback to clear filters.
     */
    const onClear = () => setSearchText('');

    const onFilter = (filters: DocumentFiltersType) => {
        setTagFilters(filters.tags?.map((tag) => tag.id) || []);
    };

    return {
        ...mediaListRest,
        currentPath,
        searchValue: searchText,
        sorts,
        fetchNextPage: handleFetchNextPage,
        onClear,
        onFilter,
        onSearch,
        onSort: setSorts,
    };
};
